import { useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import EmployerUpdateRequestForm from "@/components/employer-update-request/EmployerUpdateRequestForm";
import { useValidateCorrectionLink } from "@/hooks/correction-link/correction-link.hook";
import { toast } from "@/utils/toast";

interface OnboardingTempData {
  companyName?: string;
  employerCode?: string;
  rcNumber?: string;
  registeredAddress?: string;
  contactEmail?: string;
  contactPhoneNumber?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  contactPersonRole?: string;
}

/**
 * Onboarding (pre-auth) entry point for the employer update request flow.
 * Supports two entry points:
 * 1. State-based: `{ data: { tempData } }` (VerifyCompanyDetails) or `{ employerCode, contactEmail }` (EmployerRegistrationVerificationPage)
 * 2. Token-based: Correction link from email with `?token=ABC123` — validates token and pre-fills employer data
 */
const ReportDiscrepanciesPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const stateData = location.state as
    | ({ data?: { tempData?: OnboardingTempData } } & Partial<OnboardingTempData>)
    | null;

  const {
    data: validationData,
    isPending: isValidating,
    isError: isValidationError,
  } = useValidateCorrectionLink(token);
  const [tempData, setTempData] = useState<OnboardingTempData>({});

  useEffect(() => {
    if (token) {
      if (isValidationError) {
        toast.error(
          "Unable to validate correction link. Please try again or request a new link."
        );
        return;
      }
      if (validationData?.isValid === false) {
        toast.error(
          validationData.reason === "EXPIRED"
            ? "Correction link has expired"
            : validationData.reason === "USED"
            ? "Correction link has already been used"
            : "Invalid correction link"
        );
      }
      if (validationData?.isValid) {
        setTempData({
          employerCode: validationData.employerCode,
          companyName: validationData.currentName,
          rcNumber: validationData.currentRcNumber,
          registeredAddress: validationData.currentAddress,
          contactEmail: validationData.currentEmail,
          contactPersonEmail: validationData.currentContactPersonEmail,
          contactPersonPhone: validationData.currentContactPersonPhone,
          contactPersonRole: validationData.currentContactPersonRole,
        });
      }
    } else {
      const dataFromState: OnboardingTempData =
        stateData?.data?.tempData ??
        (stateData?.employerCode ? stateData : {}) ??
        {};
      setTempData(dataFromState);
    }
  }, [token, validationData, isValidationError, stateData]);

  if (token && isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating correction link...</p>
        </div>
      </div>
    );
  }

  return (
    <EmployerUpdateRequestForm
      context="onboarding"
      employerCode={tempData.employerCode ?? ""}
      currentName={tempData.companyName}
      currentRcNumber={tempData.rcNumber}
      currentAddress={tempData.registeredAddress}
      currentEmail={tempData.contactEmail}
      currentPhone={tempData.contactPhoneNumber}
      currentContactPersonEmail={tempData.contactPersonEmail}
      currentContactPersonPhone={tempData.contactPersonPhone}
      currentContactPersonRole={tempData.contactPersonRole}
      contactEmailDefault={tempData.contactEmail}
      correctionToken={token ?? undefined}
    />
  );
};

export default ReportDiscrepanciesPage;
